import React, { useState, useEffect, Suspense } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import * as layoutConstants from '../constants/layout';
import { Container } from 'reactstrap';
import { changeSidebarTheme, changeSidebarType } from '../redux/actions';
import { initMenu } from '../redux/appMenu/actions';
import { getLoggedInUser } from '../helpers/authUtils';
import EdgeToast from '../components/EdgeToast';

const LeftSidebar = React.lazy(() => import('../components/LeftSidebar'));
const Topbar = React.lazy(() => import('../components/Topbar'));
const Footer = React.lazy(() => import('../components/Footer'));

const emptyLoading = () => <div></div>;
const loading = () => <div className="text-center"></div>;
const Vertical = (props) => {
    const dispatch = useDispatch();
    const [children, setChildren] = useState(null);
    const [isCondensed, setIsCondensed] = useState(false);
    const [isLight, setIsLight] = useState(false);
    const layout = useSelector((state) => state.Layout);
    const { menuItems } = useSelector((state) => state.AppMenu);
    const { user } = useSelector((state) => state.Auth);

    const [showToast, setShowToast] = useState(false);
    const [toastType, setToastType] = useState('success');
    const [toastTitle, setToastTitle] = useState('');
    const [toastContent, setToastContent] = useState('');
    const [timeoutId, setTimeoutId] = useState('');
    const toggleToast = () => setShowToast(!showToast);
    const showMessageToast = (type, title, content) => {
        setToastType(type);
        setToastTitle(title);
        setToastContent(content);
        setShowToast(true);
        const tId = setTimeout(() => {
            setShowToast(false);
        }, 30000);
        setTimeoutId(tId);
    };

    // Initialize layout and menu when user is authenticated
    useEffect(() => {
        const loggedInUser = getLoggedInUser();

        if (loggedInUser && user && (!menuItems || menuItems.length === 0)) {
            // Initialize menu
            dispatch(initMenu());

            // Initialize layout
            dispatch(changeSidebarTheme(layoutConstants.LEFT_SIDEBAR_THEME_DARK));

            // activate the condensed sidebar if smaller devices like ipad or tablet
            if (window.innerWidth >= 768 && window.innerWidth <= 1028) {
                dispatch(changeSidebarType(layoutConstants.LEFT_SIDEBAR_TYPE_CONDENSED));
            }
        }
    }, [dispatch, user, menuItems]);

    useEffect(() => {
        window.addEventListener('resize', updateDimensions);

        setChildren(props.children);
        setIsCondensed(layout.leftSideBarType === layoutConstants.LEFT_SIDEBAR_TYPE_CONDENSED);
        setIsLight(layout.leftSideBarTheme === layoutConstants.LEFT_SIDEBAR_THEME_LIGHT);

        return () => {
            window.removeEventListener('resize', updateDimensions);
        };
    }, [dispatch, layout, props.children]);

    const openLeftMenu = () => {
        if (document.body) {
            const wrappers = document.getElementsByClassName('wrapper');
            if (wrappers.length) {
                wrappers[0].classList.toggle('sidebar-enable');
            }
        }
    };

    const updateDimensions = () => {
        // activate the condensed sidebar if smaller devices like ipad or tablet
        if (window.innerWidth >= 768 && window.innerWidth <= 1028) {
            dispatch(changeSidebarType(layoutConstants.LEFT_SIDEBAR_TYPE_CONDENSED));
        } else if (window.innerWidth > 1028) {
            dispatch(changeSidebarType(layoutConstants.LEFT_SIDEBAR_TYPE_FIXED));
        }
    };

    return (
        <div className="app">
            <div className="wrapper">
                <Suspense fallback={emptyLoading()}>
                    <LeftSidebar
                        {...props}
                        isCondensed={isCondensed}
                        isLight={isLight}
                        hideLogo={false}
                        hideUserProfile={true}
                    />
                </Suspense>

                <div className="content-wrapper">
                    <div className="content-page">
                        <div className="content">
                            <Suspense fallback={emptyLoading()}>
                                <Topbar {...props} openLeftMenuCallBack={openLeftMenu} hideLogo={true} />
                            </Suspense>

                            <Container fluid>
                                <Suspense fallback={loading()}>{children}</Suspense>
                            </Container>
                        </div>
                    </div>

                    <Suspense fallback={emptyLoading()}>
                        <Footer {...props} />
                    </Suspense>
                </div>
                <EdgeToast
                    isOpen={showToast}
                    toggle={toggleToast}
                    type={toastType}
                    title={toastTitle}
                    content={toastContent}
                />
            </div>
        </div>
    );
};

export default Vertical;
